import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SpotifyTrack, ReccobeatsAudioFeature } from '../types';
import { getRecommendations } from '../services/emotifyService';
import { useAppContext } from '../context/AppContext';
import { useThemeContext } from '../context/ThemeContext';
import { 
  validateRecommendationCount,
  normalizeCoordinate
} from '../utils/recommendationEngine';
import RecommendationsList from './RecommendationsList';
import AdBanner from './AdBanner';
import '../styles/RecommendationsModal.css';

type ModalView = 'settings' | 'results';

interface RecommendationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthError?: () => void;
  valence: number; // -1 to 1
  energy: number; // -1 to 1
  nearbySongs: Array<{
    id: string;
    name: string;
    artist: string;
    spotifyId: string;
  }>;
  audioFeatures: ReccobeatsAudioFeature[];
}

const RecommendationsModal: React.FC<RecommendationsModalProps> = ({
  isOpen,
  onClose,
  onAuthError,
  valence,
  energy,
  nearbySongs,
  audioFeatures,
}) => {
  // View state for two-panel layout
  const [currentView, setCurrentView] = useState<ModalView>('settings');
  
  // State management
  const [closestTrack, setClosestTrack] = useState<ReccobeatsAudioFeature | null>(null);
  const [seedTracks, setSeedTracks] = useState<ReccobeatsAudioFeature[]>([]);
  const [recommendationCount, setRecommendationCount] = useState<number>(20);
  const [recommendations, setRecommendations] = useState<SpotifyTrack[]>([]);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  
  // Loading states
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  
  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { platform } = useAppContext();
  const { mode } = useThemeContext();

  /**
   * Find multiple nearby tracks to use as seeds
   * @param targetValence Target valence (0-1)
   * @param targetEnergy Target energy (0-1)
   * @param features All audio features
   * @param count Number of seeds to find (default: 3)
   * @returns Array of nearby tracks sorted by distance
   */
  const findNearbyTracks = (
    targetValence: number,
    targetEnergy: number,
    features: ReccobeatsAudioFeature[],
    count: number = 3
  ): ReccobeatsAudioFeature[] => {
    if (!features || features.length === 0) {
      return [];
    }

    // Calculate distance for each track
    const tracksWithDistance = features.map(feature => {
      const valenceDiff = feature.valence - targetValence;
      const energyDiff = feature.energy - targetEnergy;
      const distance = Math.sqrt(valenceDiff * valenceDiff + energyDiff * energyDiff);
      return { feature, distance };
    });

    // Sort by distance and take the closest ones
    tracksWithDistance.sort((a, b) => a.distance - b.distance);
    
    return tracksWithDistance.slice(0, count).map(item => item.feature);
  };

  // Find closest tracks when modal opens
  useEffect(() => {
    if (isOpen && audioFeatures && audioFeatures.length > 0) {
      // Convert heatmap coordinates from [-1, 1] to [0, 1]
      const normalizedValence = normalizeCoordinate(valence);
      const normalizedEnergy = normalizeCoordinate(energy);
      
      // Find multiple nearby tracks (up to 5 seeds)
      const nearbyTracks = findNearbyTracks(normalizedValence, normalizedEnergy, audioFeatures, 5);
      setSeedTracks(nearbyTracks);
      
      // Keep the closest track for display
      const closest = nearbyTracks[0] || null;
      setClosestTrack(closest);
      
      if (!closest) {
        setError('Could not find suitable seed tracks for recommendations');
      }
    }
  }, [isOpen, valence, energy, audioFeatures]);

  const handleGetRecommendations = async () => {
    setIsLoadingRecommendations(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Validate recommendation count
      if (!validateRecommendationCount(recommendationCount)) {
        setError('Recommendation count must be between 1 and 100');
        setIsLoadingRecommendations(false);
        return;
      }

      // Check if we have seed tracks
      if (!seedTracks || seedTracks.length === 0) {
        setError('No seed tracks available for recommendations');
        setIsLoadingRecommendations(false);
        return;
      }

      // Get Spotify IDs from seed tracks
      const seedTrackIds = seedTracks
        .map(track => track.spotify_id || track.id)
        .filter(id => id); // Remove any null/undefined values

      // Convert heatmap coordinates from [-1, 1] to [0, 1]
      const normalizedValence = normalizeCoordinate(valence);
      const normalizedEnergy = normalizeCoordinate(energy);

      // Request double the amount to allow for sorting and filtering
      const requestSize = Math.min(recommendationCount * 2, 100); // Cap at 100

      // Fetch recommendations from ReccoBeats (pass multiple seed track IDs)
      const result = await getRecommendations(
        seedTrackIds,
        normalizedValence,
        normalizedEnergy,
        requestSize,
        platform || undefined
      );

      if (result.error) {
        // Provide more context about the error
        const errorMsg = result.error.message || 'Unknown error';
        if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
          setError('Network error: Could not connect to the recommendation service. Please check your connection.');
        } else {
          setError(`Recommendation error: ${errorMsg}`);
        }
      } else if (result.data && result.data.tracks && result.data.tracks.length > 0) {
        // Convert to SpotifyTrack format for the existing RecommendationsList component
        const allTracks: SpotifyTrack[] = result.data.tracks.map(t => ({
          id: t.id,
          name: t.name,
          artists: [{ id: '', name: t.artist }],
          album: {
            id: '',
            name: t.album,
            images: t.image ? [{ url: t.image, height: 300, width: 300 }] : [],
            release_date: '',
          },
          popularity: 0,
          platformUrl: (t as any).platformUrl || `https://open.spotify.com/track/${t.id}`,
          platformName: (t as any).platform === 'ytmusic' ? 'YouTube Music' : 'Spotify',
        }));

        const finalTracks = allTracks.slice(0, recommendationCount);

        setRecommendations(finalTracks);
        setSelectedTracks(new Set());
        setCurrentView('results');
      } else {
        setError('No recommendations found: The service could not find songs matching your criteria. Try clicking on a different area of the heatmap.');
      }
    } catch (err) {
      if (err instanceof Error) {
        const errorMsg = err.message;
        if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
          setError('Network error: Could not connect to the server. Please check your internet connection.');
        } else if (errorMsg.includes('timeout') || errorMsg.includes('Timeout')) {
          setError('Request timeout: The server took too long to respond. Please try again.');
        } else {
          setError(`Error: ${errorMsg}`);
        }
      } else {
        setError('An unexpected error occurred while getting recommendations. Please try again.');
      }
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  const handleTrackToggle = (trackId: string) => {
    const newSelected = new Set(selectedTracks);
    if (newSelected.has(trackId)) {
      newSelected.delete(trackId);
    } else {
      newSelected.add(trackId);
    }
    setSelectedTracks(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedTracks(new Set(recommendations.map(track => track.id)));
  };

  const handleDeselectAll = () => {
    setSelectedTracks(new Set());
  };

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setRecommendationCount(0);
      return;
    }
    const value = parseInt(raw, 10);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setRecommendationCount(value);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setCurrentView('settings');
    setRecommendationCount(20);
    setRecommendations([]);
    setSelectedTracks(new Set());
    setError(null);
    setSuccessMessage(null);
    setClosestTrack(null);
    onClose();
  };

  const handleBack = () => {
    setCurrentView('settings');
    setError(null);
    setSuccessMessage(null);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="modal-content" data-theme={mode} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-left">
            {currentView === 'results' && (
              <button className="back-btn" onClick={handleBack} type="button" aria-label="Back">
                <ArrowBackIcon />
              </button>
            )}
            <h2>{currentView === 'settings' ? 'Get Recommendations' : 'Recommendations'}</h2>
          </div>
          <button className="close-btn" onClick={handleClose} type="button" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          {/* Error and success messages */}
          {error && (
            <div className="message error-message">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="message success-message">
              {successMessage}
            </div>
          )}

          {/* Settings Panel */}
          {currentView === 'settings' && (
            <div className="modal-controls-wrapper">
              <div className="context-info">
                <p>Find songs with a similar emotional mood to this point on your heatmap</p>
                <div className="emotion-pills">
                  <span className="emotion-pill">Valence: {valence.toFixed(2)}</span>
                  <span className="emotion-pill">Energy: {energy.toFixed(2)}</span>
                </div>
              </div>

              {/* Recommendation count input */}
              <div className="count-input-section">
                <label htmlFor="rec-count">Number of recommendations:</label>
                <input
                  id="rec-count"
                  type="number"
                  min="1"
                  max="100"
                  value={recommendationCount || ''}
                  onChange={handleCountChange}
                  className="count-input"
                />
                {!validateRecommendationCount(recommendationCount) && (
                  <span className="validation-error">Must be between 1 and 100</span>
                )}
              </div>

              {/* Get recommendations button */}
              <button
                className="get-recommendations-btn"
                onClick={handleGetRecommendations}
                disabled={isLoadingRecommendations || !closestTrack}
                type="button"
              >
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                {isLoadingRecommendations ? 'Loading...' : 'Get Recommendations'}
              </button>
            </div>
          )}

          {/* Results Panel */}
          {currentView === 'results' && recommendations.length > 0 && (
            <RecommendationsList recommendations={recommendations} />
          )}

          {/* Ad Banner at bottom of results */}
          {currentView === 'results' && recommendations.length > 0 && (
            <AdBanner style={{ margin: '24px auto 0', maxWidth: '100%' }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationsModal;
